import { comparer } from "mobx";
import { observer } from "mobx-react-lite";
import React from "react";
import { FormGroup } from "react-bootstrap";
import { useFormContext } from "./Form";
import { useTranslation } from "./hooks/useTranslation";
import { SafeHTMLDisplay } from "./SafeHTMLDisplay";
export var EMPTY_VALUE = "-";
var hasValue = function (value) {
    return value.length;
};
export var StaticInput = observer(function (_a) {
    var name = _a.name, label = _a.label, _b = _a.type, type = _b === void 0 ? "text" : _b, formatter = _a.formatter, dataId = _a.dataId, dataTestId = _a.dataTestId;
    var values = useFormContext().values;
    var inputValue = type === "html" && values.get(name) ? (React.createElement(SafeHTMLDisplay, { html: values.get(name) })) : (React.createElement("span", { className: "form-control-static-value" }, formatter ? formatter(values.get(name)) : values.get(name)));
    return (React.createElement(RawStaticInput, { label: label, value: inputValue, type: type, dataId: dataId, dataTestId: dataTestId }));
});
export var RawStaticInput = function (_a) {
    var label = _a.label, value = _a.value, _b = _a.type, type = _b === void 0 ? "text" : _b, _c = _a.labelHidden, labelHidden = _c === void 0 ? false : _c, dataId = _a.dataId, dataTestId = _a.dataTestId;
    return (React.createElement("div", { className: "form-control-static", "data-id": dataId, "data-testid": dataTestId },
        !labelHidden && (React.createElement("span", { className: "form-control-static-label" }, label)),
        type === "html" ? (React.createElement(HTMLValueWrapper, null, value)) : (React.createElement("span", { className: "form-control-static-value", style: { all: "unset", wordBreak: "break-word" } }, value))));
};
export var StaticInputValue = function (_a) {
    var label = _a.label, staticFormatter = _a.staticFormatter, name = _a.name, _b = _a.type, type = _b === void 0 ? "text" : _b, labelHidden = _a.labelHidden, dataId = _a.dataId, dataTestId = _a.dataTestId, dataLabel = _a.dataLabel;
    var values = useFormContext().values;
    var value = values.get(name);
    var formatValue = function () {
        return staticFormatter ? staticFormatter(value) : value;
    };
    return (React.createElement(FormGroup, { "data-id": dataId && "static-".concat(dataId), "data-testid": dataTestId && "static-".concat(dataTestId), "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: value ? formatValue() : EMPTY_VALUE, type: type, labelHidden: labelHidden })));
};
export var StaticCheckbox = function (_a) {
    var label = _a.label, staticFormatter = _a.staticFormatter, name = _a.name, dataId = _a.dataId, dataTestId = _a.dataTestId, dataLabel = _a.dataLabel;
    var t = useTranslation();
    var values = useFormContext().values;
    var value = values.get(name);
    var isChecked = value === true || value === "true";
    var formatValue = function () {
        return staticFormatter ? staticFormatter(value) : t("Yes");
    };
    return (React.createElement(FormGroup, { "data-id": dataId && "static-".concat(dataId), "data-testid": dataTestId && "static-".concat(dataTestId), "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: isChecked ? formatValue() : EMPTY_VALUE })));
};
export var StaticTypeahead = function (_a) {
    var label = _a.label, staticFormatter = _a.staticFormatter, name = _a.name, multiple = _a.multiple, dataId = _a.dataId, dataTestId = _a.dataTestId, dataLabel = _a.dataLabel;
    var values = useFormContext().values;
    var value = values.get(name);
    var formatValue = function () {
        if (!value || (multiple && value && value.length === 0)) {
            return "-";
        }
        if (hasValue(value)) {
            return value.map(function (option, i) {
                if (!option.label && !staticFormatter) {
                    return undefined;
                }
                return listValues(option, i, staticFormatter);
            });
        }
        if (value && !hasValue(value)) {
            if (!value.label && !staticFormatter) {
                return undefined;
            }
            return getDisplayValueForListValues(value, staticFormatter);
        }
    };
    return (React.createElement(FormGroup, { "data-id": dataId && "static-".concat(dataId), "data-testid": dataTestId && "static-".concat(dataTestId), "data-label": dataLabel },
        React.createElement(RawStaticInput, { label: label, value: values ? formatValue() : EMPTY_VALUE })));
};
export var StaticMultipleValues = function (_a) {
    var label = _a.label, multiple = _a.multiple, isLoading = _a.isLoading, name = _a.name, options = _a.options, staticFormatter = _a.staticFormatter, labelHidden = _a.labelHidden, dataId = _a.dataId, dataTestId = _a.dataTestId, dataLabel = _a.dataLabel, className = _a.className;
    var values = useFormContext().values;
    var value = values.get(name);
    var formatValue = function () {
        if (multiple) {
            var values_1 = value.map(function (v) {
                return options.find(function (o) { return comparer.structural(o.value, v); });
            });
            if (values_1.length === 0)
                return EMPTY_VALUE;
            return values_1.map(function (value, i) {
                return listValues(value, i);
            });
        }
        var option = options.find(function (o) { return comparer.structural(o.value, value); });
        if (staticFormatter) {
            return staticFormatter(option);
        }
        return option ? option.label : value;
    };
    return (React.createElement(FormGroup, { "data-id": dataId && "static-".concat(dataId), "data-testid": dataTestId && "static-".concat(dataTestId), "data-label": dataLabel, className: className },
        React.createElement(RawStaticInput, { label: label, value: !isLoading && value !== undefined && value !== null
                ? formatValue()
                : EMPTY_VALUE, labelHidden: labelHidden })));
};
var getDisplayValueForListValues = function (value, staticFormatter) {
    if (staticFormatter) {
        return staticFormatter(value);
    }
    if (!staticFormatter && (value === null || value === void 0 ? void 0 : value.label) != null) {
        return value.label;
    }
    return EMPTY_VALUE;
};
var listValues = function (option, i, staticFormatter) {
    return "".concat(i !== 0 ? ", " : "").concat(getDisplayValueForListValues(option, staticFormatter));
};
var HTMLValueWrapper = function (_a) {
    var children = _a.children;
    return React.createElement("div", { className: "form-control-static-html" }, children);
};
